import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatLegacyTabChangeEvent } from '@angular/material/legacy-tabs';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QRCodeComponent } from 'angularx-qrcode';
import { NgxPermissionsService } from 'ngx-permissions';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { InventoryStockType } from '@app/core/_constant/index.constants';
import {
  ProductTypeConstant,
  ProductTypes,
} from '@app/core/_constant/product-type.constants';
import { MirrorInventoryFromParent } from '@app/core/_constant/product.constants';
import { SettingsKey } from '@app/core/_constant/settings.constants';
import { ViewAccountOnly } from '@app/core/_constant/user-account.constants';
import { AuthService } from '@app/core/auth';
import { PermissionService } from '@app/core/auth/services/permission.service';
import { CommentType } from '@app/core/messaging';
import { RfqService, RFQ } from '@app/core/quotes';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';
import { ExportRfqService } from '@app/shared/services/export-rfq.service';
import { trackById } from 'v2/modules/shared/util/track-by.util';

import { CreateClientRfqComponent } from '../create-client-rfq/create-client-rfq.component';
import { EditRfqVariantsComponent } from '../edit-rfq-variants/edit-rfq-variants.component';
import { AdjustmentEntryFormComponent } from '../inventory/adjustment-entry-form/adjustment-entry-form.component';
import { SkuStockDetailsComponent } from '../inventory/sku-stock-details/sku-stock-details.component';
import { RfqClientProfileComponent } from '../rfq-client-profile/rfq-client-profile.component';
import { RfqFactoryAdminAssignmentComponent } from '../rfq-factory-admin-assignment/rfq-factory-admin-assignment.component';
import { UpdateRfqLocationsComponent } from '../update-rfq-locations/update-rfq-locations.component';

import { RFQ_DETAILS_TABS, RfqDetailsTabConfig } from './rfq-details.tabs';

//

@Component({
  selector: 'cnc-rfq-details',
  templateUrl: './rfq-details.component.html',
  styleUrls: ['./rfq-details.component.scss'],
})
export class RfqDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  url :string = environment.url;
  rfq :RFQ;
  commentType :CommentType;
  subscription :Subscription;
  rfqSubscription :Subscription;
  childComponentVisible :boolean = true;
  flag :boolean = false;
  private onDestroy$ = new Subject();

  HIDE_UOM_KEY :string = 'hide_uom';

  qrCodeData :string = '';
  qrCodeFileName :string = '';

  settings :any = {};

  location :any = window.location;

  productTypes = ProductTypes;
  featureFlags :{ [key :string] :boolean };

  @ViewChild('qrcode', { static: true }) qrcode :QRCodeComponent;
  link :string;
  settingsKey :any = SettingsKey;
  onOffQuotaionsSetting :boolean = false;
  hideUom :boolean = false;

  _TABS :RfqDetailsTabConfig[] = [...RFQ_DETAILS_TABS];
  _TAB_INDEX_BY_ID :{ [key :string] :number };
  _TAB_ID_BY_INDEX :{ [key :number] :string };
  filteredTabs :RfqDetailsTabConfig[] = [];
  selectedIndex :number = 0;
  selectedTabId :(typeof RFQ_DETAILS_TABS)[number]['id'];
  trackById = trackById;

  constructor(
    public rfqService :RfqService,
    private activeRoute :ActivatedRoute,
    private modalSvc :NgbModal,
    public authService :AuthService,
    private cd :ChangeDetectorRef,
    public exportRfq :ExportRfqService,
    public settingsSvc :SettingsService,
    private toastr :ToastrService,
    public _location :Location,
    public UserPermission :PermissionService,
    private ngxPermissionsService :NgxPermissionsService,
  ) {}

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.rfqSubscription.unsubscribe();
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() {
    this.settingsSvc.masterfile$.subscribe((set :any) => {
      this.settings = set;
    });
    this.initFeatureFlags();
    this.getOnOffQuotationSetting().subscribe();
    this.getHideUomStatus();
    this.#buildTabIndexMaps();
  }

  async #initFiltereTabs() :Promise<void> {
    const tabs = await Promise.all(
      this._TABS.map(async (tab) => {
        if (!tab.permissions) return tab;
        const hasPerms = await this.ngxPermissionsService.hasPermission(
          tab.permissions,
        );

        return hasPerms ? tab : null;
      }),
    );

    this.filteredTabs = tabs.filter((tab) => tab !== null) as RfqDetailsTabConfig[];
    this.#syncSelectedIndexWithFilteredTabs();
  }

  #syncSelectedIndexWithFilteredTabs() {
    if (!this.selectedTabId && this.filteredTabs.length > 0) {
      this.selectedTabId = this.filteredTabs[0].id;
    }
    const idx = this.filteredTabs.findIndex(tab => tab.id === this.selectedTabId);
    this.selectedIndex = idx !== -1 ? idx : 0;
  }

  private reInitiateChildComponent() :void {
    this.childComponentVisible = false;
    this.cd.detectChanges();
    this.childComponentVisible = true;
    this.cd.detectChanges();
  }

  private getQueryParams() :Observable<any> {
    return this.activeRoute.queryParams.pipe(
      tap((value :any) => {
        if (value) {
          this.flag = value.flag;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getHideUomStatus() :void {
    this.settingsSvc
      .getKeyValue(this.HIDE_UOM_KEY)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp :any) => {
        if (resp) {
          this.hideUom = resp;
        }
        this.cd.detectChanges();
      });
  }

  ngAfterViewInit() {
    this.subscription = this.activeRoute.params.subscribe((params) => {
      if (!params || !params.id) {
        return;
      }
      this.rfqService.setActiveById(params.id);
      this.getRfqDetails(params.id);
    });

    this.getQueryParams().subscribe();
    this.#initializeActiveTabSubscription();
  }

  getRfqDetails(id :number) {
    this.rfqSubscription = this.rfqService
      .getById(id, true)
      .subscribe((rfq :any) => {
        if (rfq && rfq.id) {
          try {
            rfq.unit_costs = this.rfqService.getSkuUnitCosts(rfq);
            if (!Array.isArray(rfq.product_variants)) {
              rfq.product_variants = JSON.parse(rfq.product_variants);
            }
          } catch (e) {}

          try {
            rfq.product_link = JSON.parse(rfq.product_link);
          } catch (e) {
            if (rfq.product_link && typeof rfq.product_link == 'string') {
              rfq.product_link = [{ lv: rfq.product_link }];
            }
          }
          try {
            rfq.custom_fields = JSON.parse(rfq.custom_fields);
          } catch (e) {}
          try {
            rfq.materials = JSON.parse(rfq.materials);
          } catch (e) {}

          this.qrCodeData = location.origin + '/inventory/' + rfq.id + '/view';
          this.qrCodeFileName = rfq.internal_sku + '-qr.png';
          this.commentType = {
            ref_tab_type: 1,
            ref_tab_id: rfq.id,
          };

          rfq.pendingQuantity =
            rfq.stock && rfq.stock.qty_pending ? rfq.stock.qty_pending : 0;

          this.rfq = rfq;
          this.#buildTabs();
          this.#initFiltereTabs();
          if (this.flag) {
            this.reInitiateChildComponent();
          }
          this.cd.detectChanges();
        }
      });
  }

  exportRfqToPDF(rfq :any) {
    this.exportRfq.exportRFQtoPDF(rfq, this.settings.LOGISTICS);
  }

  createAdjustmentEntry() {
    const modalRef = this.modalSvc.open(AdjustmentEntryFormComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.componentInstance.isModal = true;
    modalRef.result.then((data) => {
      if (data && data.id) {
        this.rfqService.getById(this.rfq.id, true).subscribe((resp :any) => {
          if (resp && resp.stock) {
            this.rfq.stock = resp.stock;
            this.cd.detectChanges();
          }
        });
      }
    });
  }

  editRfq() {
    const modalRef = this.modalSvc.open(CreateClientRfqComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.rfq = this.rfq;
    modalRef.result.then((data) => {
      if (data) {
        this.rfq = data;
        this.cd.detectChanges();
      }
    });
  }

  editVariants() {
    const modalRef = this.modalSvc.open(EditRfqVariantsComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.rfq = this.rfq;
  }

  viewClientInfo() {
    const modalRef = this.modalSvc.open(RfqClientProfileComponent, {});
    modalRef.componentInstance.rfq = this.rfq;
  }

  assignRFQ() {
    const modalRef = this.modalSvc.open(RfqFactoryAdminAssignmentComponent, {});
    modalRef.componentInstance.rfq = this.rfq;
  }

  transferRfq() {
    const modalRef = this.modalSvc.open(UpdateRfqLocationsComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.rfq = this.rfq;
  }

  exportQrCode() {
    const rfq = this.rfq;
    const qrcode = document.getElementById('qrcode');
    const svg = qrcode.firstChild.firstChild;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const data = new XMLSerializer().serializeToString(svg);
    const DOMURL :any = window.URL || (window as any).webkitURL || window;

    const img = new Image();
    const svgBlob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const url = DOMURL.createObjectURL(svgBlob);

    img.onload = function () {
      ctx.drawImage(img, 0, 0);
      DOMURL.revokeObjectURL(url);

      const imgURI = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');

      const evt = new MouseEvent('click', {
        view: window,
        bubbles: false,
        cancelable: true,
      });

      const a = document.createElement('a');
      a.setAttribute('download', rfq.internal_sku + '-qr.png');
      a.setAttribute('href', imgURI);
      a.setAttribute('target', '_blank');

      a.dispatchEvent(evt);
    };

    img.src = url;
  }

  dlQrCode() :void {
    this.link = this.qrcode.qrcElement.nativeElement.firstChild.src;
  }

  discontinue(rfq :RFQ) {
    this.rfqService.discontinue(rfq.id, 1).subscribe((response :any) => {
      this.rfq.is_discontinued = 1;
      this.cd.detectChanges();
      this.toastr.success('Successfully discontinued SKU.', 'Success');
    });
  }

  continue(rfq :RFQ) {
    this.rfqService.discontinue(rfq.id, 0).subscribe((response :any) => {
      this.rfq.is_discontinued = 0;
      this.cd.detectChanges();
      this.toastr.success('Successfully reactivated SKU.', 'Success');
    });
  }

  viewPendingRfqStockDetails(rfq :RFQ) {
    const modalRef = this.modalSvc.open(SkuStockDetailsComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.rfq = rfq;
    modalRef.componentInstance.qtyType = InventoryStockType.PENDING;
  }

  get isKitItems() :boolean {
    return this.rfq.type === this.productTypes.KIT;
  }

  checkIfCanCreateAdjustmentEntry() :boolean {
    return (
      this.authService.loginUser.prwx === ViewAccountOnly.NO &&
      this.rfq &&
      this.rfq.mirror_inventory_from_parent !== MirrorInventoryFromParent.YES &&
      (this.rfq.type === ProductTypeConstant.PRODUCT || this.rfq.type === null)
    );
  }

  private getOnOffQuotationSetting() :Observable<any> {
    return this.settingsSvc.getKeyValue(this.settingsKey.ON_OFF_QUOTATIONS).pipe(
      tap((setValue) => {
        if (setValue) {
          this.onOffQuotaionsSetting = setValue === true ? true : false;
          this.#buildTabs();
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  promoteVariantToMasterSku(rfq :RFQ) :void {
    this.rfqService
      .promoteVariantToMasterSku({ rfqIds: [rfq.id] })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (response :any) => {
          this.toastr.success(
            'Successfully promoted Variant SKU to Master SKU',
            'Success',
          );
          this.rfq.variant_parent_id = 0;
          this.cd.detectChanges();
        },
        (error) => {
          this.toastr.error(error?.error?.error);
        },
      );
  }

  onSyncBomRfqs(bomRfqs :any) :void {
    if (bomRfqs && bomRfqs.length > 0) {
      bomRfqs = bomRfqs.map((bomRfq :any) => {
        bomRfq.bom_id = bomRfq.bomId;
        bomRfq.rfq_id = bomRfq.rfqId;

        return bomRfq;
      });
    }
    this.rfq.bom_rfqs = bomRfqs;
    this.cd.detectChanges();
  }

  private initFeatureFlags() :void {
    this.featureFlags = this.activeRoute.snapshot.data['featureFlags'];
  }

  get ftSupplierSourceSku() :boolean {
    if (
      this.featureFlags &&
      Object.hasOwn(this.featureFlags, 'ftSupplierSourceSku')
    ) {
      return this.featureFlags['ftSupplierSourceSku'];
    }

    return false;
  }

  #isTabVisible(tab :RfqDetailsTabConfig) :boolean {
    if (!this.rfq) {
      return false;
    }

    switch (tab.id) {
      case 'kit-items':
        return this.isKitItems;
      case 'variants':
        return !this.rfq?.variant_parent_id;
      case 'quotations':
        return this.onOffQuotaionsSetting;
      case 'sales':
      case 'cogs-margin':
        return this.rfq.product_type_id !== 3;
      case 'costs':
        return this.rfq.product_type_id !== 3 && !this.isKitItems;
      case 'qty-logs':
        return this.rfq.type === this.productTypes.PRODUCT;
      case 'supplier-skus':
        return (
          this.ftSupplierSourceSku && this.rfq.type === this.productTypes.PRODUCT
        );
      default:
        return true;
    }
  }

  onTabChange(e :MatLegacyTabChangeEvent) {
    this.selectedTabId = this._TAB_ID_BY_INDEX[this.selectedIndex];
  }

  #activateSelectedTab() {
    this.#syncSelectedIndexWithFilteredTabs();
  }

  #buildTabIndexMaps() :void {
    this._TAB_INDEX_BY_ID = Object.fromEntries(
      this._TABS.map((tab) => [tab.id, tab.index]),
    );
    this._TAB_ID_BY_INDEX = Object.fromEntries(
      this._TABS.map((tab) => [tab.index, tab.id]),
    );
  }
  #buildTabs() {
    this._TABS = [];

    let index = 0;

    RFQ_DETAILS_TABS.forEach((tab :RfqDetailsTabConfig) => {
      if (this.#isTabVisible(tab)) {
        this._TABS.push({ ...tab, index });
        index++;
      }
    });
    this.#buildTabIndexMaps();
    this.#activateSelectedTab();
  }

  #initializeActiveTabSubscription() {
    this.activeRoute.queryParams
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((params) => {
        this.selectedTabId = params?.v;
        this.#activateSelectedTab();
      });
  }
}
